import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  UsersIcon,
  CurrencyRupeeIcon,
  PercentBadgeIcon,
} from "@heroicons/react/24/outline";
import discountApi, { Discount } from "../../api/discount";
import Sidebar from "../../components/common/Sidebar";
import Header from "../../components/common/Header";

const DiscountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [discount, setDiscount] = useState<Discount | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDiscount();
    }
  }, [id]);

  const fetchDiscount = async () => {
    try {
      setLoading(true);
      const response = await discountApi.getDiscount(id!);
      setDiscount(response.discount);
    } catch (error) {
      console.error("Error fetching discount:", error);
      navigate("/discounts");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    if (!discount) return;

    setUpdating(true);
    try {
      if (discount.isActive) {
        await discountApi.deactivateDiscount(discount._id);
      } else {
        await discountApi.activateDiscount(discount._id);
      }

      setDiscount((prev) =>
        prev ? { ...prev, isActive: !prev.isActive } : null,
      );
    } catch (error) {
      console.error("Error updating discount status:", error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUsagePercentage = (usedBy: string[], maxUsage: number | null) => {
    if (!maxUsage) return { text: "Unlimited", percentage: 0 };
    const percentage = Math.round((usedBy.length / maxUsage) * 100);
    return { text: `${usedBy.length}/${maxUsage}`, percentage };
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!discount) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Discount not found</div>
        <Link
          to="/discounts"
          className="mt-4 inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Discounts
        </Link>
      </div>
    );
  }

  const usage = getUsagePercentage(discount.usedBy, discount.maxUsage);

  return (
    <div className="flex">
      <Sidebar drawerWidth={250} />
      <div className="flex-1">
        <Header drawerWidth={250} />
        <div className="p-6 mt-20 m-200">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/discounts"
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Discounts
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Discount Details
                </h1>
                <p className="text-gray-600">
                  View and manage discount information
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleStatusToggle}
                disabled={updating}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  discount.isActive
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-green-600 text-white hover:bg-green-700"
                } ${updating ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {updating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : discount.isActive ? (
                  <XCircleIcon className="h-4 w-4 mr-2" />
                ) : (
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                )}
                {discount.isActive ? "Deactivate" : "Activate"}
              </button>
              <Link
                to={`/discounts/${discount._id}/edit`}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Discount Code Card */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Discount Code
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code
                    </label>
                    <div className="text-lg font-mono bg-gray-100 px-3 py-2 rounded border">
                      {discount.code}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(discount.isActive)}`}
                    >
                      {discount.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Discount Details Card */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Discount Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <div className="flex items-center">
                      {discount.discountType === "flat" ? (
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      ) : (
                        <PercentBadgeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <span className="capitalize">
                        {discount.discountType}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <div className="text-lg font-semibold">
                      {discount.discountType === "flat" ? "₹" : ""}
                      {discount.value}
                      {discount.discountType === "percent" ? "%" : ""}
                    </div>
                  </div>
                  {discount.minOrderValue && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Order Value
                      </label>
                      <div className="text-lg">₹{discount.minOrderValue}</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Time Plan Subscribers Only
                    </label>
                    <div className="text-lg">
                      {discount.firstTimeUserOnly ? "Yes" : "No"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Allow Multiple Use
                    </label>
                    <div className="text-lg">
                      {discount.allowMultipleUse ? "Yes" : "No"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instant Discount (Applicable to Kinnow Instant orders only)
                    </label>
                    <div className="text-lg">
                      {discount.forInstant ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Attached Bonus Card */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Attached Bonus
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <div className="text-lg">{discount.attachedTo.name}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bonus Type
                    </label>
                    <div className="flex items-center">
                      {discount.attachedTo.bonusType === "flat" ? (
                        <CurrencyRupeeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      ) : (
                        <PercentBadgeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <span className="capitalize">
                        {discount.attachedTo.bonusType}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bonus Value
                    </label>
                    <div className="text-lg font-semibold">
                      {discount.attachedTo.bonusType === "flat" ? "₹" : ""}
                      {discount.attachedTo.value}
                      {discount.attachedTo.bonusType === "percent" ? "%" : ""}
                    </div>
                  </div>
                  {discount.attachedTo.totalBonus && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Bonus
                      </label>
                      <div className="text-lg">
                        ₹{discount.attachedTo.totalBonus}
                      </div>
                    </div>
                  )}
                  {discount.attachedTo.minBonus && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Bonus
                      </label>
                      <div className="text-lg">
                        ₹{discount.attachedTo.minBonus}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            {discount.termsAndConditions &&
              discount.termsAndConditions.length > 0 && (
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Terms and Conditions
                  </h2>
                  <ul className="list-disc pl-5 space-y-1">
                    {discount.termsAndConditions.map((term, index) => (
                      <li key={index} className="text-gray-700">
                        {term}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Usage Statistics */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Usage Statistics
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usage
                    </label>
                    <div className="text-lg font-semibold">{usage.text}</div>
                    {discount.maxUsage && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min(usage.percentage, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {usage.percentage}% used
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Users Used
                    </label>
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg">{discount.usedBy.length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Important Dates
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Created
                    </label>
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm">
                        {formatDate(discount.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Updated
                    </label>
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm">
                        {formatDate(discount.updatedAt)}
                      </span>
                    </div>
                  </div>
                  {discount.expiresAt && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expires
                      </label>
                      <div className="flex items-center">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm">
                          {formatDate(discount.expiresAt)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountDetail;
